import React, { useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import logo from "../../assets/logos/TRANSKYRIOS-_IconColor.avif";
import "./informe-mantencion.css";
import "./informe-taller-pasos.css";

// Componente UI
import Stepper from '../../components/stepper/stepper';
import { Toast } from 'primereact/toast';

// Datos
import revisionService from '../../services/revisionService.js';
import { useInformeTallerState } from '../../hooks/panel_taller/useInformeTallerState.js';

// Componentes
import RecepcionInicial from '../../components/formulario-tallermec/recepcion-inicial';
import DiagnoReparacion from '../../components/formulario-tallermec/diagno-reparacion';
import FirmasConforme from '../../components/formulario-tallermec/firmas-conforme';
import ModuleLayout from '../../layouts/layout-modulo.js';
import LoaderOverlay from '../../components/loader/loader.js';


const PASOS = [
  { label: 'Recepción Inicial', icon: 'pi pi-car' },
  { label: 'Diagnóstico y Reparación', icon: 'pi pi-wrench' },
  { label: 'Firmas de Conformidad',icon: 'pi pi-pencil' },
];

function InformeTallerPasos() {
  const toast = useRef(null);
  const navigate = useNavigate();

  // /app/taller/revisiones/paso1  -> sin :id, informe nuevo
  // /app/taller/revisiones/:id/paso1 -> informe existente, abre en el paso indicado
  let params = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const pasoInicial = Number(searchParams.get('paso') ?? 0);

  const [estado,dispatch] = useInformeTallerState(params.revisionId ?? null, pasoInicial);
  const { revisionId, informe, cargando, pasoActivo } = estado;

  // Cargar informe (F5, link, navegación)
  useEffect(() => {
    if (!params.revisionId) return;

    /**
     * Si el id de la URL ya coincide con el que tenemos en el estado Y ya
     * tenemos sus datos, es porque los acabamos de guardar/recibir nosotros
     * mismos (p.ej. justo después de RegistrarRecepcion) — no hay que
     * recargar ni mostrar el loader de pantalla completa.
     */
    const datosFrescos = String(params.revisionId) === String(revisionId) && informe;
    
    if (datosFrescos) return;

    dispatch({type: 'EMPEZAR_CARGA'});

    revisionService.obtenerRevision(params.revisionId)
      .then((data) => {
        const revision = data.revision ?? data;
        dispatch({type: 'REVISION_CARGADA', id: params.revisionId, informe: revision });
      })
      .catch((err) => {
        console.error('Error cargando revisión:', err);
        dispatch({ type: 'ERROR_CARGA' });
        toast.current?.show({
          severity: 'error',
          summary: 'Error al cargar',
          detail: 'No se pudo cargar la revisión.',
          life: 4000,
        });
      });
  }, [params.revisionId, revisionId, informe, dispatch]);

  /**
   * ## Regla de edición (recepción / taller / firmas)
   * Editable mientras taller y firmas NO estén completos. 
   * Una vez los dos se completan, todo el informe (los 3 pasos) queda de solo lectura.
   */
  const soloLectura = Boolean(informe && ( 
    informe.estado_taller === 'completo' && informe.estado_firmas === 'completo'
  ));

  /**
   * Navegación manual del usuario entre pasos (Stepper, "paso anterior").
   * Se valida contra el revisionId ACTUAL del estado — como esto se llama
   * desde un click nuevo del usuario (no encadenado a un dispatch anterior en
   * la misma función), no hay riesgo de clausura vieja.
   */
  const irAPaso = useCallback((paso) => {
    if (!revisionId && paso > 0) {
      toast.current?.show({
        severity: 'warn',
        summary: 'Recepción requerida',
        detail: 'Primero debes guardar la Recepción Inicial.',
        life: 4000,
      });
      return;
    }

    dispatch({ type: 'CAMBIAR_PASO', paso });
    setSearchParams({ paso: String(paso) });
  }, [revisionId, setSearchParams, dispatch]);

  // ---------------- Paso 1: Recepción ----------------
  const RegistrarRecepcion = useCallback(async (payload) => {
    try {
      const resultado = await revisionService.guardarPaso1(revisionId, payload);
      const nuevoId = resultado?.paso_uno?.revision?.id_revision ?? revisionId;
      
      const data = await revisionService.obtenerRevision(nuevoId);
      const revisionActualizada = data.revision ?? data;

      /**
       * Una sola acción: guarda el id, el informe y avanza al paso 1.
       * Ya no depende de que revisionId (estado) se haya "alcanzado a
       * actualizar" antes de decidir a qué paso ir.
       */
      dispatch({
        type: 'PASO_GUARDADO',
        id: nuevoId,
        informe: revisionActualizada,
        siguientePaso: 1,
      });

      toast.current?.show({
        severity: 'success',
        summary: 'Paso 1: Recepción ',
        detail: `Informe guardado correctamente`,
        life: 3000,
      });

      setSearchParams({ paso: '1' });

      // Si era nuevo, actualiza la URL para que quede en modo "edición de informe existente"
      if (!revisionId) {
        navigate(`/app/taller/revisiones/${nuevoId}?paso=1`, 
        { replace: true });
      }

    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error al registrar',
        detail: typeof err === 'string' ? err : `ERROR.`,
        life: 5000,
      });
    }
  }, [revisionId, navigate, setSearchParams, dispatch]);

  // ---------------- Paso 2: Diagnóstico y Reparación ----------------
  const GuardarDiagnostico = useCallback(async (payload) => {
    try {
      await revisionService.guardarPaso2(revisionId, payload);
      
      const data = await revisionService.obtenerRevision(revisionId);
      const revisionActualizada = data.revision ?? data;

      dispatch({
        type: 'PASO_GUARDADO',
        id: revisionId,
        informe: revisionActualizada,
        siguientePaso: 2,
      });

      toast.current?.show({
        severity: 'success',
        summary: 'Paso 2: Taller',
        detail: 'Guardado correctamente',
        life: 3000,
      });

      setSearchParams({ paso: '2' });

    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error al guardar paso 2',
        detail: typeof err === 'string' ? err : `ERROR.`,
        life: 5000,
      });
    }
  }, [revisionId, setSearchParams, dispatch]);

  // ---------------- Paso 3: Firmas ----------------
  const FinalizarFirmas = useCallback(async (payload) => {
    
    try {
      await revisionService.guardarPaso3(revisionId, payload);
      
      const data = await revisionService.obtenerRevision(revisionId);
      const revisionActualizada = data.revision ?? data;

      dispatch({
        type: 'PASO_GUARDADO',
        id: revisionId,
        informe: revisionActualizada,
      });
      
      toast.current?.show({
        severity: 'success',
        summary: 'Paso 3: Firmas',
        detail: 'Guardado correctamente',
        life: 3000,
      });

    } catch (err) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error al guardar las firmas',
        detail: typeof err === 'string' ? err : `ERROR.`,
        life: 5000,
      });
    }
  }, [revisionId, dispatch]);

  if (cargando) {
    return (
      <ModuleLayout titulo="Informe de Vehículo" logo={logo} onVolver={() => navigate('/app/taller')}>
        <div className="d-flex justify-content-center mt-5">
          <LoaderOverlay />
        </div>
      </ModuleLayout>
    );
  }

  return (
    <ModuleLayout titulo="Informe de Vehículo" logo={logo} onVolver={() => navigate('/app/taller/seguimiento-informes')}>
      <Toast ref={toast} position="center" />
      
      <section className="seccion-steps">
          <Stepper 
            steps={PASOS}
            activeStep={pasoActivo}
            onStepClick={irAPaso} // clickeable en cualquier orden
          />

          {pasoActivo === 0 && (
            <RecepcionInicial 
              onRegistrar={RegistrarRecepcion} 
              valoresIniciales={informe}
              soloLectura={soloLectura}
            />
          )}

          {pasoActivo === 1 && (
            <DiagnoReparacion 
              revisionId={revisionId}
              onGuardar={GuardarDiagnostico}
              onVolver={() => irAPaso(0)}
              valoresIniciales={informe}
              soloLectura={soloLectura}
            />
          )}

          {pasoActivo === 2 && (
            <FirmasConforme 
              revisionId={revisionId}
              onFinalizar={FinalizarFirmas}
              onVolver={() => irAPaso(1)}
              valoresIniciales={informe}
              soloLectura={soloLectura}
            />
          )}
      </section>
    </ModuleLayout>
  );
}

export default InformeTallerPasos;